import React, { useCallback, useMemo, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Checkbox from '@atlaskit/checkbox/checkbox';
import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';
import { JiraIssueTypeIcon } from '@atlassian/jira-platform-fields/JiraIssueTypeIcon.tsx';

import type { GicModalProps } from '../../../../../common/ui/gic-modal';
import {
	isSubTaskWithoutCreatedParent,
	isValidJiraIssueKey,
} from '../../../../../common/utils/jira-create-with-rovo-preview-card';

import type { workItemsTable_AtlassianConversationAssistantPreviewCard_project$key as WorkItemsTableProjectKey } from './__generated__/workItemsTable_AtlassianConversationAssistantPreviewCard_project.graphql';
import type { workItemsTable_AtlassianConversationAssistantPreviewCard_Row_issueType$key as RowIssueTypeKey } from './__generated__/workItemsTable_AtlassianConversationAssistantPreviewCard_Row_issueType.graphql';
import type { workItemsTable_AtlassianConversationAssistantPreviewCard_Row_project$key as RowProjectKey } from './__generated__/workItemsTable_AtlassianConversationAssistantPreviewCard_Row_project.graphql';
import type { workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_issueType$key as RowWithWrapperIssueTypeKey } from './__generated__/workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_issueType.graphql';
import type { workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_project$key as RowWithWrapperProjectKey } from './__generated__/workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_project.graphql';
import { RowActions } from './row-actions';

const messages = defineMessages({
	checkMarkIcon: {
		id: 'conversation-assistant-preview-card.conversation-assistant-preview-card.jira-create-with-rovo-preview-card.jira-multi-work-item-card.work-items-table.checkMarkIcon',
		defaultMessage: 'Work item created',
		description: 'Label for the icon indicating a work item has been created',
	},
	showMoreSuggestions: {
		id: 'conversation-assistant-preview-card.conversation-assistant-preview-card.jira-create-with-rovo-preview-card.jira-multi-work-item-card.work-items-table.showMoreSuggestions',
		defaultMessage: 'Show {count} more suggestions',
		description:
			'Accessible label for the button to show more work item suggestions in the multi work item preview card',
	},
	showFewerSuggestions: {
		id: 'conversation-assistant-preview-card.conversation-assistant-preview-card.jira-create-with-rovo-preview-card.jira-multi-work-item-card.work-items-table.showFewerSuggestions',
		defaultMessage: 'Show fewer suggestions',
		description:
			'Accessible label for the button to collapse the work item suggestions list in the multi work item preview card',
	},
	disabledCheckboxSubTask: {
		id: 'conversation-assistant-preview-card.conversation-assistant-preview-card.jira-create-with-rovo-preview-card.jira-multi-work-item-card.work-items-table.disabledCheckboxSubTask',
		defaultMessage: 'Select a parent to select this work item.',
		description:
			'Tooltip text for the disabled checkbox of a child work item with a work item type that requires a parent work item',
	},
	selectWorkItem: {
		id: 'conversation-assistant-preview-card.conversation-assistant-preview-card.jira-create-with-rovo-preview-card.jira-multi-work-item-card.work-items-table.selectWorkItem',
		defaultMessage: 'Select {summary}',
		description: 'Accessible label for the checkbox to select a work item',
	},
});

const styles = cssMap({
	table: {
		position: 'relative',
		width: '100%',
		borderRadius: token('radius.small'),
	},
	tableWithPadding: {
		paddingBottom: token('space.100'),
	},
	row: {
		position: 'relative',
		borderRadius: token('radius.small'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	fadeOffZoneTruncated: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '32px',
		backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), ${token('color.background.input')} 100%)`,
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	// Bleed the fade zone out past the parent container's space.100 padding.
	fadeOffZoneTruncatedBleed: {
		left: token('space.negative.100'),
		right: token('space.negative.100'),
		bottom: token('space.negative.100'),
	},
	// In the staging area (preview card), apply border radius to match the card's rounded corners.
	fadeOffZoneTruncatedStagingArea: {
		borderBottomLeftRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
	},
	fadeOffZoneExpanded: {
		backgroundImage: 'none',
		height: '12px',
	},
	issueKeyWrapper: {
		flexShrink: 0,
		whiteSpace: 'nowrap',
	},
	summaryWrapper: {
		flex: 1,
		minWidth: '0px',
	},
	summaryWithActions: {
		marginRight: token('space.1000'),
	},
	pressable: {
		width: '100%',
		display: 'block',
		textAlign: 'left',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	showMoreButton: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.input'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		zIndex: 100,
		opacity: 0,
		transition: 'opacity 0s',
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.input.pressed'),
		},
	},
	showMoreButtonVisible: {
		opacity: 1,
		transition: 'opacity 0.15s ease-in-out',
	},
	showMoreButtonElevated: {
		position: 'relative',
		top: token('space.negative.200'),
	},
	checkboxWrapper: {
		display: 'flex',
		alignItems: 'center',
		marginBlockStart: token('space.negative.050'),
		marginBlockEnd: token('space.negative.050'),
		marginInlineStart: token('space.negative.050'),
		marginInlineEnd: token('space.negative.050'),
	},
	hidden: {
		display: 'none',
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
	},
	rowDisabled: {
		opacity: token('opacity.disabled'),
		pointerEvents: 'none',
	},
});

type RowProps = {
	hideInlineButtons: boolean;
	onDeclineWorkItem: (args: { invocationId: string }) => void;
	gicEntryPointProps: GicModalProps;
	onInlineAccept: (args: { draftWorkItem: DraftWorkItem }) => Promise<void>;
	isSelected: boolean;
	onToggleSelect: (invocationId: string, newValue: boolean) => void;
	isRowDisabled: boolean;
	checkboxesMode: boolean;
	isCreating: boolean;
	isParentSelected: boolean;
};

const Row = ({
	project,
	issueType,
	draftWorkItem,
	hideInlineButtons,
	onDeclineWorkItem,
	gicEntryPointProps,
	onInlineAccept,
	isStagingArea,
	itemCount,
	isSelected,
	onToggleSelect,
	isRowDisabled,
	checkboxesMode,
	isCreating: isCreatingAll,
	isParentSelected,
	sendAifcAnalyticsEvent,
}: {
	project: RowProjectKey;
	issueType?: RowIssueTypeKey | null;
	draftWorkItem: DraftWorkItem;
	isStagingArea: boolean;
	itemCount: number;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
} & RowProps) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	const { summary } = draftWorkItem;

	const isCreated = Boolean(draftWorkItem.createdIssueKey);
	const showRowActions = isHovered && !hideInlineButtons && !isCreated && !isCreatingAll;

	const handleInlineAccept = useCallback(async () => {
		setIsCreating(true);
		const parent = draftWorkItem.fields.parent as string;
		const draftWorkItemToAccept = { ...draftWorkItem };
		if (parent && !isValidJiraIssueKey(parent)) {
			draftWorkItemToAccept.fields.parent = undefined;
		}
		if (!parent) {
			// remove the child work items for the inline create
			draftWorkItemToAccept.childWorkItems = [];
		}
		await onInlineAccept({ draftWorkItem: draftWorkItemToAccept });
		setIsCreating(false);
	}, [onInlineAccept, draftWorkItem]);

	const handleDeclineWorkItem = useCallback(
		(args: { invocationId: string }) => {
			if (
				FeatureGates.getExperimentValue<string>(
					'jira-aifc-workflow-resumption-ui-updates',
					'cohort',
					'control',
				) === 'checkboxes'
			) {
				onToggleSelect(draftWorkItem.invocationId, false);
				draftWorkItem.childWorkItems?.forEach((child) => onToggleSelect(child.invocationId, false));
			}
			onDeclineWorkItem(args);
		},
		[onDeclineWorkItem, onToggleSelect, draftWorkItem.invocationId, draftWorkItem.childWorkItems],
	);

	// Keep the summary truncated (room reserved for the action buttons) while creating,
	// even when the row is no longer hovered, so the text never overlaps the buttons.
	const reserveActionSpace = showRowActions || isCreating;

	const projectRef = useFragment<RowProjectKey>(
		graphql`
			fragment workItemsTable_AtlassianConversationAssistantPreviewCard_Row_project on JiraProject {
				...rowActions_AtlassianConversationAssistantPreviewCard
			}
		`,
		project,
	);

	const issueTypeData = useFragment<RowIssueTypeKey>(
		graphql`
			fragment workItemsTable_AtlassianConversationAssistantPreviewCard_Row_issueType on JiraIssueType {
				...JiraIssueTypeIcon_Platform
			}
		`,
		issueType,
	);

	return (
		<Box
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			xcss={isRowDisabled ? styles.rowDisabled : undefined}
		>
			<Inline alignBlock="center" space="space.075" xcss={cx(styles.row)}>
				{isCreated ? (
					<Inline alignBlock="center" space="space.050">
						<CheckMarkIcon
							color={token('color.icon.success')}
							label={formatMessage(messages.checkMarkIcon)}
						/>
						<Box xcss={styles.issueKeyWrapper}>
							<Text color="color.text.subtlest">{draftWorkItem.createdIssueKey}</Text>
						</Box>
					</Inline>
				) : FeatureGates.getExperimentValue<string>(
						'jira-aifc-workflow-resumption-ui-updates',
						'cohort',
						'control',
				  ) === 'checkboxes' ? (
					<>
						<Box xcss={isHovered || checkboxesMode ? styles.checkboxWrapper : styles.hidden}>
							<Tooltip
								content={
									isSubTaskWithoutCreatedParent(draftWorkItem) && !isParentSelected
										? formatMessage(messages.disabledCheckboxSubTask)
										: undefined
								}
							>
								<Checkbox
									isChecked={isSelected}
									isDisabled={
										isCreatingAll ||
										(isSubTaskWithoutCreatedParent(draftWorkItem) && !isParentSelected)
									}
									onChange={() => {
										onToggleSelect(draftWorkItem.invocationId, !isSelected);
										sendAifcAnalyticsEvent?.({
											action: 'selected',
											actionSubject: 'option',
											actionSubjectId: 'createWorkItemsSuggestionToggled',
											attributes: {
												isSelected: !isSelected,
												invocationId: draftWorkItem.invocationId,
											},
										});
									}}
									aria-label={formatMessage(messages.selectWorkItem, { summary })}
								/>
							</Tooltip>
						</Box>
						<Box xcss={isHovered || checkboxesMode ? styles.hidden : styles.iconWrapper}>
							{issueTypeData && <JiraIssueTypeIcon issueType={issueTypeData} />}
						</Box>
					</>
				) : (
					issueTypeData && <JiraIssueTypeIcon issueType={issueTypeData} />
				)}
				<Box xcss={cx(styles.summaryWrapper, reserveActionSpace && styles.summaryWithActions)}>
					<Text maxLines={1}>{summary}</Text>
				</Box>
				<RowActions
					projectRef={projectRef}
					draftWorkItem={draftWorkItem}
					onDeclineWorkItem={handleDeclineWorkItem}
					onInlineAccept={handleInlineAccept}
					isCreating={isCreating}
					showRowActions={showRowActions}
					gicEntryPointProps={gicEntryPointProps}
					isStagingArea={isStagingArea}
					itemCount={itemCount}
					sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
				/>
			</Inline>
		</Box>
	);
};

const ShowMoreButton = ({
	remainingCount,
	isTruncated,
	onToggle,
	isStagingArea,
}: {
	remainingCount: number;
	isTruncated: boolean;
	onToggle: () => void;
	isStagingArea: boolean;
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={cx(
				styles.fadeOffZoneTruncated,
				!isTruncated && styles.fadeOffZoneExpanded,
				styles.fadeOffZoneTruncatedBleed,
				isStagingArea && styles.fadeOffZoneTruncatedStagingArea,
			)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Pressable
				xcss={cx(
					styles.showMoreButton,
					isHovered && styles.showMoreButtonVisible,
					!isTruncated && styles.showMoreButtonElevated,
				)}
				onClick={onToggle}
				aria-label={
					FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
						fireExperimentExposure: false,
					})
						? isTruncated
							? formatMessage(messages.showMoreSuggestions, { count: remainingCount })
							: formatMessage(messages.showFewerSuggestions)
						: undefined
				}
			>
				{isTruncated ? `Show ${remainingCount} more` : 'Show less'}
			</Pressable>
		</Box>
	);
};

const RowWithWrapper = ({
	openWorkItemView,
	draftWorkItem,
	project,
	issueType,
	isStagingArea,
	...props
}: {
	draftWorkItem: DraftWorkItem;
	project: RowWithWrapperProjectKey;
	issueType: RowWithWrapperIssueTypeKey | null;
	openWorkItemView: (createdIssueKey?: string) => void;
	isStagingArea: boolean;
	itemCount: number;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
} & RowProps) => {
	const onCardClick = useCallback(() => {
		openWorkItemView(draftWorkItem.createdIssueKey);
	}, [openWorkItemView, draftWorkItem.createdIssueKey]);

	const rowRef = useFragment<RowWithWrapperProjectKey>(
		graphql`
			fragment workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_project on JiraProject {
				...workItemsTable_AtlassianConversationAssistantPreviewCard_Row_project
			}
		`,
		project,
	);
	const issueTypeData = useFragment<RowWithWrapperIssueTypeKey>(
		graphql`
			fragment workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_issueType on JiraIssueType {
				...workItemsTable_AtlassianConversationAssistantPreviewCard_Row_issueType
			}
		`,
		issueType,
	);

	const rowElement =
		!draftWorkItem.createdIssueKey || isStagingArea ? (
			<Row
				project={rowRef}
				issueType={issueTypeData}
				draftWorkItem={draftWorkItem}
				isStagingArea={isStagingArea}
				{...props}
			/>
		) : (
			<Pressable onClick={onCardClick} xcss={styles.pressable}>
				<Row
					project={rowRef}
					issueType={issueTypeData}
					draftWorkItem={draftWorkItem}
					isStagingArea={isStagingArea}
					{...props}
				/>
			</Pressable>
		);

	if (
		FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
			fireExperimentExposure: false,
		})
	) {
		return <Box role="listitem">{rowElement}</Box>;
	}

	return rowElement;
};

/**
 * A simple component that renders a list of Jira work item suggestions.
 * Truncates this list at 5 items - if there are more than 5, then show a simple fade-off effect to indicate that there are more items.
 *
 * @param draftWorkItems - The list of work item suggestions to render.
 */

export type WorkItemsTableProps = {
	project: WorkItemsTableProjectKey;
	draftWorkItems: DraftWorkItem[];
	openWorkItemView: (createdIssueKey?: string) => void;
	isStagingArea: boolean;
	selectedItems: Set<string>;
	onToggleSelect: (invocationId: string, newValue: boolean) => void;
	hasAnyCreatedItems: boolean;
	isCreating: boolean;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
} & Omit<
	RowProps,
	'isSelected' | 'onToggleSelect' | 'isRowDisabled' | 'checkboxesMode' | 'isParentSelected'
>;

export const WorkItemsTable = ({
	project,
	draftWorkItems: initialDraftWorkItems,
	hideInlineButtons,
	onDeclineWorkItem,
	onInlineAccept,
	openWorkItemView,
	gicEntryPointProps,
	isStagingArea,
	selectedItems,
	onToggleSelect,
	hasAnyCreatedItems,
	isCreating,
	sendAifcAnalyticsEvent,
}: WorkItemsTableProps): React.JSX.Element => {
	const flattenedDraftWorkItems = initialDraftWorkItems.flatMap((draftWorkItem) => [
		draftWorkItem,
		...(draftWorkItem.childWorkItems ?? []),
	]);

	const childToParentMap = useMemo(
		() =>
			new Map(
				initialDraftWorkItems.flatMap((parent) =>
					(parent.childWorkItems ?? []).map((child) => [child.invocationId, parent.invocationId]),
				),
			),
		[initialDraftWorkItems],
	);

	const firstFiveWorkItems = flattenedDraftWorkItems.slice(0, 5);
	const remainingWorkItemsCount = flattenedDraftWorkItems.length - 5;
	const itemCount = flattenedDraftWorkItems.length;

	const [showTruncatedWorkItemsList, setShowTruncatedWorkItemsList] = useState(itemCount > 5);
	const workItemsToShow = showTruncatedWorkItemsList ? firstFiveWorkItems : flattenedDraftWorkItems;

	const workItemsTableRef = useFragment<WorkItemsTableProjectKey>(
		graphql`
			fragment workItemsTable_AtlassianConversationAssistantPreviewCard_project on JiraProject {
				issueTypes {
					edges {
						node {
							issueTypeId
							...workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_issueType
						}
					}
				}

				...workItemsTable_AtlassianConversationAssistantPreviewCard_RowWithWrapper_project
			}
		`,
		project,
	);
	const issueTypeMap = useMemo(
		() =>
			new Map(
				workItemsTableRef.issueTypes?.edges?.flatMap((edge) =>
					edge?.node ? [[edge.node.issueTypeId, edge.node]] : [],
				),
			),
		[workItemsTableRef.issueTypes?.edges],
	);

	const isListSemanticsEnabled = FeatureGates.getExperimentValue(
		'jira-aifc-a11y-fixes-preview-card',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

	const rows = workItemsToShow.map((draftWorkItem) => {
		return (
			<RowWithWrapper
				project={workItemsTableRef}
				issueType={issueTypeMap.get(draftWorkItem.issueTypeId) ?? null}
				key={draftWorkItem.invocationId}
				draftWorkItem={draftWorkItem}
				hideInlineButtons={hideInlineButtons}
				openWorkItemView={openWorkItemView}
				onDeclineWorkItem={onDeclineWorkItem}
				gicEntryPointProps={gicEntryPointProps}
				onInlineAccept={onInlineAccept}
				isStagingArea={isStagingArea}
				itemCount={itemCount}
				isSelected={selectedItems.has(draftWorkItem.invocationId)}
				onToggleSelect={onToggleSelect}
				checkboxesMode={selectedItems.size > 0}
				isParentSelected={selectedItems.has(childToParentMap.get(draftWorkItem.invocationId) ?? '')}
				isCreating={
					isCreating &&
					FeatureGates.getExperimentValue<string>(
						'jira-aifc-workflow-resumption-ui-updates',
						'cohort',
						'control',
						{ fireExperimentExposure: false },
					) === 'checkboxes'
				}
				isRowDisabled={
					FeatureGates.getExperimentValue<string>(
						'jira-aifc-workflow-resumption-ui-updates',
						'cohort',
						'control',
						{ fireExperimentExposure: false },
					) === 'checkboxes'
						? // Row disabled post-creation
							(hasAnyCreatedItems && !draftWorkItem.createdIssueKey) ||
							// Row disabled during creation
							(isCreating && !selectedItems.has(draftWorkItem.invocationId))
						: false
				}
				sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
			/>
		);
	});

	return (
		<Stack xcss={cx(styles.table, !showTruncatedWorkItemsList && styles.tableWithPadding)}>
			{isListSemanticsEnabled ? <Stack role="list">{rows}</Stack> : rows}
			{remainingWorkItemsCount > 0 && (
				<ShowMoreButton
					remainingCount={remainingWorkItemsCount}
					isTruncated={showTruncatedWorkItemsList}
					onToggle={() => setShowTruncatedWorkItemsList((prev) => !prev)}
					isStagingArea={isStagingArea}
				/>
			)}
		</Stack>
	);
};
